import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { authAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, LogIn } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.login(formData);

      // ✅ New backend format validation
      if (!response?.success || !response?.user || !response?.token) {
        throw new Error('Invalid server response');
      }

      const { user, token } = response;

      // ✅ Ensure this page is only for Job Seekers
      if (user.role !== 'job_seeker') {
        toast({
          title: 'Access Denied',
          description: 'This login is for Job Seekers only',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      // ✅ Store auth data
      localStorage.setItem('authToken', token);
      localStorage.setItem('userEmail', user.email);
      localStorage.setItem('userFullName', user.fullName || '');
      localStorage.setItem('userRole', user.role);
      localStorage.setItem('companyId', user.companyId || '');

      toast({
        title: 'Success!',
        description: 'Login successful',
      });

      navigate('/');
      window.location.reload();

    } catch (error) {
      console.error('Login error:', error);

      toast({
        title: 'Login Failed',
        description:
          error?.detail ||
          error?.message ||
          'Invalid email or password',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-md mx-auto px-6 py-20">
        <Card className="p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogIn className="w-8 h-8 text-[#4a90e2]" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Welcome Back
            </h1>
            <p className="text-gray-600">
              Login to your job seeker account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                required
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className="mt-1"
              />
              {/* Forgot Password */}
            <div className="flex justify-end mt-2">
            <Link
                to="/forgot-password"
                className="text-sm text-[#4a90e2] hover:underline"
                >
                Forgot Password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#4a90e2] hover:bg-[#3a7bc8] text-white font-semibold h-12"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="text-[#4a90e2] hover:underline font-medium"
              >
                Register here
              </Link>
            </p>
          </div>
        </Card>
      </div>

      <Footer />
    </div>
  );
};

export default Login;